/**
 * 保障小測驗 — the business-side sibling of CareerQuiz.
 *
 * A client plays a short personality game; the adviser gets back, without the
 * client ever seeing the words, a DISC letter, what they got wrong about cover,
 * what they are curious about, and which objections they are likely to raise.
 *
 * Rules that shape every question here:
 *  - **Fun first.** Each question is something people answer about themselves
 *    for fun (旅行你負責甚麼、突然收到 10 萬元).
 *  - **Short.** A6 A7 C5 D3 E2 and the contact step were cut to keep the quiz
 *    around two minutes; records taken before that still carry their answers,
 *    which the readers below simply ignore.
 *  - **Two personas.** A parent and an individual worry about different things,
 *    so the first question asks which one the client is, and the questions whose
 *    answers differ (C3 C6 D1 D2 E1) carry one option set per persona. The
 *    client picks a finer role (初出社會、二人世界、學童家長…); each role maps
 *    onto one of the two personas.
 *  - **Store answers, derive signals.** The stored result is the raw answers;
 *    `coverQuizSignals` turns them into what Smart IDEA reads, so the rules can
 *    change without invalidating a quiz someone already took.
 *
 * Question ids match the question bank the set was chosen from (mockup
 * 「客戶保障小測驗」): A1 A2 A5 · B2 · C2 C3 C6 · D1 D2 · E1 E3 E4, with G3
 * (answer reactions) on the page, plus this quiz's own: B7 B8 B9 (理財常識) and
 * S1 S4 S5 (你希望的服務方式). B3 (越遲投保越便宜, read as a push to buy) and the
 * F swipe cards were dropped on 2026-10-01; B6 (冷靜期) on 2026-10-08. Results
 * answered before then may still carry `trueFalse.B6` — it is no longer read.
 */

import type { ClientObjectionId } from '../../components/ClientEntry/SmartIdeaDialog/clientObjections';
import type { CoverGoalAnswers } from './coverGoalData';
import { DISC_EMOJI, discName } from '../../constants/disc';
import { L, listJoin } from './quizLang';
import type { QuizLang, Txt } from './quizLang';
import type { DiscLetter } from '../../constants/disc';

export type { DiscLetter };
export type CoverPersona = 'parent' | 'individual';

export type QuizOption = {
  key: string;
  icon: string;
  label: Txt;
  /** Answers this letter outright (2 points). */
  disc?: DiscLetter;
  /** Leans towards these letters (1 point each). */
  leans?: DiscLetter[];
};

/** A one-of-N pick. `options` is shared, `byPersona` replaces it per persona. */
export type PickQuestion = {
  id: string;
  question: Txt;
  hint?: Txt;
  options?: QuizOption[];
  byPersona?: Record<CoverPersona, QuizOption[]>;
};

/** A pick-several question (C6 D1 D2). `max` caps it; `none` is exclusive. */
export type MultiQuestion = PickQuestion & { max?: number };

export type TrueFalseQuestion = {
  id: string;
  statement: Txt;
  answer: boolean;
  /** Shown to the client right after they answer. */
  fact: Txt;
  /** Shown to the adviser when the client got it wrong — a way into the meeting. */
  talkingPoint: Txt;
};

export const optionsFor = (question: PickQuestion, persona: CoverPersona | undefined): QuizOption[] =>
  (question.byPersona && question.byPersona[persona || 'individual']) || question.options || [];

// ── 身分 — asked first, picks the option sets below ──────────────────────────
/**
 * The finer life stage the client picks. Each role belongs to one of the two
 * personas, which is still what picks the option sets and goals; the role only
 * sharpens the wording (the report headline, the adviser's life stage).
 */
export type CoverRole = 'fresh' | 'single' | 'couple' | 'youngKids' | 'schoolKids' | 'preRetire';

export const ROLE_OPTIONS: { key: CoverRole; persona: CoverPersona; icon: string; label: Txt; hint: Txt }[] = [
  {
    key: 'fresh',
    persona: 'individual',
    icon: '🎓',
    label: L('初出社會', '初出社会', 'Starting my career'),
    hint: L('工作幾年，剛開始儲錢', '工作几年，刚开始储钱', 'Working a few years, just started saving')
  },
  {
    key: 'single',
    persona: 'individual',
    icon: '🙋',
    label: L('單身', '单身', 'Single'),
    hint: L('一個人生活，自由自在', '一个人生活，自由自在', 'Living on my own, free and easy')
  },
  {
    key: 'couple',
    persona: 'individual',
    icon: '💑',
    label: L('二人世界', '二人世界', 'Couple'),
    hint: L('已婚或同居，暫未有孩子', '已婚或同居，暂未有孩子', 'Married or living together, no children yet')
  },
  {
    key: 'youngKids',
    persona: 'parent',
    icon: '👶',
    label: L('幼兒家長', '幼儿家长', 'Parent of young kids'),
    hint: L('孩子未上小學', '孩子未上小学', 'Children not yet in primary school')
  },
  {
    key: 'schoolKids',
    persona: 'parent',
    icon: '🎒',
    label: L('學童家長', '学童家长', 'Parent of school kids'),
    hint: L('孩子讀小學或中學', '孩子读小学或中学', 'Children in primary or secondary school')
  },
  {
    key: 'preRetire',
    persona: 'individual',
    icon: '🌅',
    label: L('準備退休', '准备退休', 'Nearing retirement'),
    hint: L('50 歲以上，子女已長大', '50 岁以上，子女已长大', 'Over 50, children grown up')
  }
];

export const roleOption = (role?: CoverRole) => ROLE_OPTIONS.find(o => o.key === role);

/** Records from before the roles were added carry only the persona. */
export const PERSONA_OPTIONS: { key: CoverPersona; icon: string; label: Txt; hint: Txt }[] = [
  {
    key: 'parent',
    icon: '👨‍👩‍👧',
    label: L('有小朋友的家長', '有小朋友的家长', 'Parent'),
    hint: L('家中有孩子', '家中有孩子', 'Children at home')
  },
  {
    key: 'individual',
    icon: '🙋',
    label: L('個人', '个人', 'Individual'),
    hint: L('單身或二人世界', '单身或二人世界', 'Single or a couple')
  }
];

// ── A · 性格（暗中推斷 DISC）──────────────────────────────────────────────────
export const PERSONALITY_QUESTIONS: PickQuestion[] = [
  {
    id: 'A1',
    question: L('朋友聚會，你通常是？', '朋友聚会，你通常是？', "At a get-together with friends, you're usually…"),
    options: [
      {
        key: 'out',
        icon: '🎤',
        label: L('帶動氣氛的那位', '带动气氛的那位', 'The one livening things up'),
        leans: ['D', 'I']
      },
      {
        key: 'in',
        icon: '🎧',
        label: L('安靜聆聽的那位', '安静聆听的那位', 'The one quietly listening'),
        leans: ['S', 'C']
      }
    ]
  },
  {
    id: 'A2',
    question: L('買一部新手機，你會？', '买一部新手机，你会？', 'Buying a new phone, you…'),
    options: [
      {
        key: 'logic',
        icon: '📊',
        label: L('比較規格才決定', '比较规格才决定', 'Compare the specs before deciding'),
        leans: ['D', 'C']
      },
      {
        key: 'feel',
        icon: '💫',
        label: L('看得順眼就買', '看得顺眼就买', 'Buy the one that looks right'),
        leans: ['I', 'S']
      }
    ]
  },
  {
    id: 'A5',
    question: L(
      '一班朋友去旅行，你負責甚麼？',
      '一班朋友去旅行，你负责什么？',
      "On a trip with friends, what's your job?"
    ),
    options: [
      {
        key: 'lead',
        icon: '🗺️',
        label: L('帶隊安排行程', '带队安排行程', 'Leading and planning the itinerary'),
        disc: 'D'
      },
      {
        key: 'vibe',
        icon: '📸',
        label: L('拍照帶動氣氛', '拍照带动气氛', 'Taking photos and keeping it fun'),
        disc: 'I'
      },
      { key: 'care', icon: '🧳', label: L('照顧大家', '照顾大家', 'Looking after everyone'), disc: 'S' },
      { key: 'plan', icon: '📑', label: L('做攻略比較', '做攻略比较', 'Researching and comparing options'), disc: 'C' }
    ]
  }
];

// ── B · 理財常識（真假挑戰）— facts to be checked by compliance before release ──
// Finance first, insurance after: the intro promises 理財常識 (2026-10-01).
export const TRUE_FALSE_QUESTIONS: TrueFalseQuestion[] = [
  {
    id: 'B7',
    statement: L(
      '投資先蝕 50%，之後再賺 50%，就可以回本。',
      '投资先蚀 50%，之后再赚 50%，就可以回本。',
      "If an investment loses 50% and then gains 50%, you're back where you started."
    ),
    answer: false,
    fact: L(
      '不會！100 萬蝕一半剩 50 萬，再賺 50% 只有 75 萬。蝕了 50%，要賺 100% 才能回本。',
      '不会！100 万蚀一半剩 50 万，再赚 50% 只有 75 万。蚀了 50%，要赚 100% 才能回本。',
      'No! $1M that loses half leaves $500K, and a 50% gain only brings it to $750K. After losing 50%, you need to gain 100% to break even.'
    ),
    talkingPoint: L(
      '他以為蝕了再賺同樣百分比就回本：可以談風險管理，和「先求不蝕」的規劃。',
      '他以为蚀了再赚同样百分比就回本：可以谈风险管理，和「先求不蚀」的规划。',
      'Thinks the same percentage gain makes up for a loss: talk about risk management and planning that avoids losses first.'
    )
  },
  {
    id: 'B9',
    statement: L(
      '每月儲 5,000 元，假設每年回報 5%，30 年後大約有 400 萬。',
      '每月储 5,000 元，假设每年回报 5%，30 年后大约有 400 万。',
      'Saving $5,000 a month at an assumed 5% a year gives about $4M after 30 years.'
    ),
    answer: true,
    fact: L(
      '是真的！30 年的本金只是 180 萬，在複利下大約累積到 416 萬（假設回報固定、每月複利，只作說明）。',
      '是真的！30 年的本金只是 180 万，在复利下大约累积到 416 万（假设回报固定、每月复利，只作说明）。',
      'True! 30 years of savings is only $1.8M, but compounding grows it to about $4.16M (assuming a fixed return compounded monthly, for illustration only).'
    ),
    talkingPoint: L(
      '他低估了複利：可以談「越早開始、每月定額」的儲蓄計劃。',
      '他低估了复利：可以谈「越早开始、每月定额」的储蓄计划。',
      'Underestimates compounding: talk about savings plans that start early with a fixed monthly amount.'
    )
  },
  {
    id: 'B2',
    statement: L(
      '醫療保險和危疾保險一樣，都是按單據報銷住院費用。',
      '医疗保险和危疾保险一样，都是按单据报销住院费用。',
      'Medical insurance and critical illness insurance both reimburse hospital bills against receipts.'
    ),
    answer: false,
    fact: L(
      '不一樣！醫療保險按單據報銷住院和手術費用；危疾保險在確診指定疾病時一筆過賠償，錢可以自由運用，例如補貼休養期間的收入。',
      '不一样！医疗保险按单据报销住院和手术费用；危疾保险在确诊指定疾病时一笔过赔偿，钱可以自由运用，例如补贴休养期间的收入。',
      'Not the same! Medical insurance reimburses hospital and surgery bills against receipts; critical illness insurance pays a lump sum when a covered illness is diagnosed, to use freely, for example to replace income while recovering.'
    ),
    talkingPoint: L(
      '他分不清醫療和危疾：可以先說明兩者的分別，再看他缺哪一種。',
      '他分不清医疗和危疾：可以先说明两者的分别，再看他缺哪一种。',
      'Mixes up medical and critical illness cover: explain the difference first, then see which one is missing.'
    )
  },
  {
    id: 'B8',
    statement: L(
      '3 個月定期存款，年利率 4%，到期時可以收到本金 4% 的利息。',
      '3 个月定期存款，年利率 4%，到期时可以收到本金 4% 的利息。',
      'A 3-month time deposit at 4% a year pays 4% of the deposit in interest at maturity.'
    ),
    answer: false,
    fact: L(
      '不是！年利率要按存款期計算，3 個月大約只有 1%（4% × 3/12）。比較存款時，記得看清楚存款期。',
      '不是！年利率要按存款期计算，3 个月大约只有 1%（4% × 3/12）。比较存款时，记得看清楚存款期。',
      'No! An annual rate is prorated over the term, so 3 months earns only about 1% (4% × 3/12). When comparing deposits, check the term.'
    ),
    talkingPoint: L(
      '他以為年利率就是到期收到的利息：可以用簡單例子，講清楚回報怎樣計算。',
      '他以为年利率就是到期收到的利息：可以用简单例子，讲清楚回报怎样计算。',
      'Thinks the annual rate is what is paid at maturity: use a simple example to show how returns are worked out.'
    )
  }
];

// ── C2 · C3 · 生活快問 ──────────────────────────────────────────────
export const LIFE_QUESTIONS: PickQuestion[] = [
  {
    id: 'C2',
    question: L('突然收到 10 萬元，你會？', '突然收到 10 万元，你会？', 'You suddenly receive $100,000. You…'),
    options: [
      { key: 'enjoy', icon: '✈️', label: L('去旅行享受', '去旅行享受', 'Go travelling and enjoy it'), leans: ['I'] },
      { key: 'save', icon: '🏦', label: L('存起來安心', '存起来安心', 'Save it for peace of mind'), leans: ['C'] },
      { key: 'invest', icon: '📈', label: L('拿去投資', '拿去投资', 'Invest it'), leans: ['D'] },
      { key: 'family', icon: '👨‍👩‍👧', label: L('給家人用', '给家人用', 'Give it to the family'), leans: ['S'] }
    ]
  },
  {
    id: 'C3',
    question: L(
      '十年後的你，最想過哪種生活？',
      '十年后的你，最想过哪种生活？',
      'Ten years from now, what life would you most like?'
    ),
    byPersona: {
      parent: [
        {
          key: 'kidsUni',
          icon: '🎓',
          label: L('孩子入讀理想大學', '孩子入读理想大学', 'My children at the university they want')
        },
        { key: 'biggerHome', icon: '🏡', label: L('一家人住大一點', '一家人住大一点', 'A bigger home for the family') },
        {
          key: 'earlyRetire',
          icon: '🏝️',
          label: L('提早退休陪家人', '提早退休陪家人', 'Retire early to be with family')
        },
        {
          key: 'familyTravel',
          icon: '🌍',
          label: L('一家人周遊列國', '一家人周游列国', 'Travel the world as a family')
        }
      ],
      individual: [
        { key: 'earlyRetire', icon: '🏝️', label: L('提早退休', '提早退休', 'Retire early') },
        { key: 'travel', icon: '🌍', label: L('周遊列國', '周游列国', 'Travel the world') },
        { key: 'career', icon: '💼', label: L('事業更上一層', '事业更上一层', 'Take my career up a level') },
        { key: 'ownHome', icon: '🏠', label: L('擁有自己的家', '拥有自己的家', 'Own my own home') }
      ]
    }
  }
];

// ── E · 作決定的方式（異議訊號）─────────────────────────────────────────────
export const DECISION_QUESTIONS: PickQuestion[] = [
  {
    id: 'E1',
    question: L('作大決定前，你通常會？', '作大决定前，你通常会？', 'Before a big decision, you usually…'),
    byPersona: {
      parent: [
        { key: 'self', icon: '🙋', label: L('自己決定', '自己决定', 'Decide myself') },
        { key: 'partner', icon: '💑', label: L('和另一半商量', '和另一半商量', 'Talk it over with my partner') },
        { key: 'family', icon: '👨‍👩‍👧', label: L('問家人意見', '问家人意见', 'Ask my family') },
        { key: 'expert', icon: '🧑‍💼', label: L('問專業人士', '问专业人士', 'Ask a professional') }
      ],
      individual: [
        { key: 'self', icon: '🙋', label: L('自己決定', '自己决定', 'Decide myself') },
        { key: 'partner', icon: '💑', label: L('和另一半商量', '和另一半商量', 'Talk it over with my partner') },
        { key: 'parents', icon: '👴', label: L('問父母意見', '问父母意见', 'Ask my parents') },
        { key: 'friends', icon: '👥', label: L('問朋友意見', '问朋友意见', 'Ask my friends') },
        {
          key: 'research',
          icon: '🔎',
          label: L('先上網查資料比較', '先上网查资料比较', 'Research and compare online first')
        },
        { key: 'expert', icon: '🧑‍💼', label: L('問專業人士', '问专业人士', 'Ask a professional') }
      ]
    }
  },
  {
    id: 'E3',
    question: L('每月開支，你會？', '每月开支，你会？', 'Your monthly spending, you…'),
    options: [
      { key: 'ledger', icon: '📒', label: L('有記帳', '有记账', 'Keep a record'), leans: ['C'] },
      { key: 'rough', icon: '🧠', label: L('心中有數', '心中有数', 'Have a rough idea') },
      { key: 'unaware', icon: '🤷', label: L('月尾才知道', '月尾才知道', 'Only find out at month end') }
    ]
  },
  {
    id: 'E4',
    question: L(
      '聽到「保險」兩個字，你的第一反應？',
      '听到「保险」两个字，你的第一反应？',
      'When you hear the word "insurance", your first reaction is…'
    ),
    hint: L('坦白說，沒有對錯 😄', '坦白说，没有对错 😄', "Be honest, there's no wrong answer 😄"),
    options: [
      { key: 'calm', icon: '😌', label: L('安心', '安心', 'Reassured') },
      { key: 'complex', icon: '🤔', label: L('有點複雜', '有点复杂', "It's a bit complicated") },
      { key: 'pushy', icon: '😅', label: L('怕被推銷', '怕被推销', 'Worried about a sales pitch') },
      { key: 'claims', icon: '🙄', label: L('理賠很麻煩', '理赔很麻烦', 'Claims are a hassle') }
    ]
  }
];

// ── C6 · 最想了解的保障（選 2）───────────────────────────────────────────────
export const INTEREST_QUESTION: MultiQuestion = {
  id: 'C6',
  question: L('你最想多了解哪方面？', '你最想多了解哪方面？', 'What would you most like to learn more about?'),
  max: 2,
  byPersona: {
    parent: [
      { key: 'medical', icon: '🏥', label: L('醫療', '医疗', 'Medical') },
      { key: 'ci', icon: '🎗️', label: L('危疾', '危疾', 'Critical illness') },
      { key: 'life', icon: '🛡️', label: L('人壽', '人寿', 'Life') },
      { key: 'edu', icon: '🎓', label: L('子女教育', '子女教育', "Children's education") },
      { key: 'kids', icon: '👶', label: L('兒童保障', '儿童保障', 'Cover for children') },
      { key: 'retire', icon: '🌅', label: L('退休', '退休', 'Retirement') },
      { key: 'mortgage', icon: '🏠', label: L('按揭保障', '按揭保障', 'Mortgage protection') },
      { key: 'accident', icon: '✈️', label: L('意外／旅遊', '意外／旅游', 'Accident / travel') }
    ],
    individual: [
      { key: 'medical', icon: '🏥', label: L('醫療', '医疗', 'Medical') },
      { key: 'ci', icon: '🎗️', label: L('危疾', '危疾', 'Critical illness') },
      { key: 'life', icon: '🛡️', label: L('人壽', '人寿', 'Life') },
      { key: 'retire', icon: '🌅', label: L('退休', '退休', 'Retirement') },
      { key: 'savings', icon: '💰', label: L('儲蓄', '储蓄', 'Savings') },
      { key: 'invest', icon: '📈', label: L('投資增值', '投资增值', 'Growing investments') },
      { key: 'mortgage', icon: '🏠', label: L('按揭保障', '按揭保障', 'Mortgage protection') },
      { key: 'accident', icon: '✈️', label: L('意外／旅遊', '意外／旅游', 'Accident / travel') }
    ]
  }
};

// ── D1 · D2 · 生活與家庭（多選）──────────────────────────────────────────────
export const FAMILY_QUESTIONS: MultiQuestion[] = [
  {
    id: 'D1',
    question: L(
      '最近一年，生活有甚麼新變化？',
      '最近一年，生活有什么新变化？',
      'Any changes in your life over the past year?'
    ),
    byPersona: {
      parent: [
        { key: 'newborn', icon: '👶', label: L('迎接新成員', '迎接新成员', 'A new family member') },
        { key: 'school', icon: '🎒', label: L('孩子升學', '孩子升学', 'Children moving up a school') },
        { key: 'home', icon: '🏠', label: L('買樓或換樓', '买楼或换楼', 'Buying or changing home') },
        { key: 'job', icon: '💼', label: L('轉職', '转职', 'Changing jobs') },
        { key: 'business', icon: '🚀', label: L('創業', '创业', 'Starting a business') },
        { key: 'none', icon: '🙂', label: L('沒有特別', '没有特别', 'Nothing in particular') }
      ],
      individual: [
        {
          key: 'relationship',
          icon: '💍',
          label: L('結婚或開始戀愛', '结婚或开始恋爱', 'Getting married or in a new relationship')
        },
        { key: 'home', icon: '🏠', label: L('買樓或搬家', '买楼或搬家', 'Buying a home or moving') },
        { key: 'job', icon: '💼', label: L('轉職', '转职', 'Changing jobs') },
        { key: 'business', icon: '🚀', label: L('創業', '创业', 'Starting a business') },
        { key: 'study', icon: '🎓', label: L('進修', '进修', 'Further studies') },
        { key: 'none', icon: '🙂', label: L('沒有特別', '没有特别', 'Nothing in particular') }
      ]
    }
  },
  {
    id: 'D2',
    question: L('家中誰最依靠你？', '家中谁最依靠你？', 'Who at home relies on you most?'),
    byPersona: {
      parent: [
        { key: 'kids', icon: '👶', label: L('小朋友', '小朋友', 'My children') },
        { key: 'partner', icon: '💑', label: L('另一半', '另一半', 'My partner') },
        { key: 'parents', icon: '👴', label: L('父母', '父母', 'My parents') },
        { key: 'pet', icon: '🐶', label: L('毛孩', '毛孩', 'My pet') }
      ],
      individual: [
        { key: 'parents', icon: '👴', label: L('父母', '父母', 'My parents') },
        { key: 'partner', icon: '💑', label: L('另一半', '另一半', 'My partner') },
        { key: 'pet', icon: '🐶', label: L('毛孩', '毛孩', 'My pet') },
        { key: 'none', icon: '🙋', label: L('暫時沒有', '暂时没有', 'No one for now') }
      ]
    }
  }
];

// ── S · 你希望的服務方式 ──────────────────────────────────────────────────────
/**
 * How the client wants to be looked after — asked as the adviser wanting to
 * serve them well, not as a form. `short` is how the adviser reads the answer.
 * S4's wording leans DISC like the personality questions do.
 */
/**
 * 你希望的服務方式 — a quick read of how the client likes to be served, not
 * the point of the quiz, so three questions only (2026-10-01; 檢視頻率 and 方便時間
 * were dropped). Ids keep their original numbers.
 */
export const SERVICE_QUESTIONS: (PickQuestion & { short: Txt })[] = [
  {
    id: 'S1',
    short: L('聯絡方式', '联络方式', 'Contact'),
    question: L(
      '你最喜歡顧問怎樣聯絡你？',
      '你最喜欢顾问怎样联络你？',
      'How would you like your adviser to contact you?'
    ),
    options: [
      { key: 'whatsapp', icon: '💬', label: L('WhatsApp 訊息', 'WhatsApp 讯息', 'WhatsApp message') },
      { key: 'call', icon: '📞', label: L('打電話', '打电话', 'Phone call') },
      { key: 'meet', icon: '☕', label: L('見面傾', '见面倾', 'Meet in person') },
      { key: 'email', icon: '📧', label: L('電郵', '电邮', 'Email') }
    ]
  },
  {
    id: 'S4',
    short: L('資料形式', '资料形式', 'Information style'),
    question: L(
      '顧問給你資料時，你最喜歡哪一種？',
      '顾问给你资料时，你最喜欢哪一种？',
      'When your adviser gives you information, you prefer…'
    ),
    options: [
      {
        key: 'brief',
        icon: '⚡',
        label: L('一兩句講重點', '一两句讲重点', 'The key points in a sentence or two'),
        leans: ['D']
      },
      {
        key: 'chat',
        icon: '😄',
        label: L('輕鬆傾，舉生活例子', '轻松倾，举生活例子', 'A relaxed chat with everyday examples'),
        leans: ['I']
      },
      {
        key: 'slow',
        icon: '🐢',
        label: L('一步一步慢慢講', '一步一步慢慢讲', 'Step by step, taking it slowly'),
        leans: ['S']
      },
      {
        key: 'detail',
        icon: '📊',
        label: L('圖表和詳細比較', '图表和详细比较', 'Charts and detailed comparisons'),
        leans: ['C']
      }
    ]
  },
  {
    id: 'S5',
    short: L('最重視', '最重视', 'Values most'),
    question: L('你最重視顧問哪一點？', '你最重视顾问哪一点？', 'What do you value most in an adviser?'),
    options: [
      { key: 'fast', icon: '⏱️', label: L('回覆快', '回复快', 'Quick replies') },
      { key: 'clear', icon: '💡', label: L('講解清楚', '讲解清楚', 'Clear explanations') },
      { key: 'noPush', icon: '🤝', label: L('不催促、不推銷', '不催促、不推销', 'No rushing, no hard sell') },
      { key: 'followUp', icon: '🔁', label: L('長期跟進', '长期跟进', 'Long-term follow-up') }
    ]
  }
];

/** 理財性格 — how the client reads their DISC letter. Names (per language) and emoji from constants/disc. */
export const COVER_ANIMALS: Record<
  DiscLetter,
  { emoji: string; name: Txt; color: string; bg: string; desc: Txt; tip: Txt }
> = {
  D: {
    emoji: DISC_EMOJI.D,
    name: L(discName('D'), discName('D', 'zh'), discName('D', 'en')),
    color: '#b83f24',
    bg: '#fdeee9',
    desc: L(
      '你重視效率和結果，喜歡直接看重點，再自己拍板。',
      '你重视效率和结果，喜欢直接看重点，再自己拍板。',
      'You value efficiency and results, like to get straight to the point, then make the call yourself.'
    ),
    tip: L(
      '先看清楚兩三個方案的比較，你會很快找到答案。',
      '先看清楚两三个方案的比较，你会很快找到答案。',
      "Compare two or three options side by side and you'll find your answer quickly."
    )
  },
  I: {
    emoji: DISC_EMOJI.I,
    name: L(discName('I'), discName('I', 'zh'), discName('I', 'en')),
    color: '#8a6508',
    bg: '#fdf6e1',
    desc: L(
      '你重視感覺和人際關係，喜歡輕鬆地了解新事物。',
      '你重视感觉和人际关系，喜欢轻松地了解新事物。',
      'You value feelings and relationships, and like to learn new things in a relaxed way.'
    ),
    tip: L(
      '用生活例子去了解保障，會比看條款更容易明白。',
      '用生活例子去了解保障，会比看条款更容易明白。',
      'Everyday examples will make cover easier to understand than reading the terms.'
    )
  },
  S: {
    emoji: DISC_EMOJI.S,
    name: L(discName('S'), discName('S', 'zh'), discName('S', 'en')),
    color: '#15803d',
    bg: '#e8f8ed',
    desc: L(
      '你重視家人和安全感，喜歡一步一步想清楚。',
      '你重视家人和安全感，喜欢一步一步想清楚。',
      'You value family and security, and like to think things through step by step.'
    ),
    tip: L(
      '先處理最影響家人的保障，其他可以慢慢來。',
      '先处理最影响家人的保障，其他可以慢慢来。',
      'Sort out the cover that matters most to your family first; the rest can wait.'
    )
  },
  C: {
    emoji: DISC_EMOJI.C,
    name: L(discName('C'), discName('C', 'zh'), discName('C', 'en')),
    color: '#1d5fa8',
    bg: '#e8f2fc',
    desc: L(
      '你重視數據和細節，作決定前會仔細比較。',
      '你重视数据和细节，作决定前会仔细比较。',
      'You value data and detail, and compare carefully before deciding.'
    ),
    tip: L(
      '要求書面資料和比較表，你會更安心。',
      '要求书面资料和比较表，你会更安心。',
      "Ask for written material and comparison tables; you'll feel more at ease."
    )
  }
};

/**
 * Everything the client answered. Optional throughout: a result is only
 * written at the end, but readers must survive older or partial records.
 */
export type CoverQuizAnswers = {
  persona?: CoverPersona;
  /** The finer 身分; its persona is `persona`. Absent on older records. */
  role?: CoverRole;
  picks?: Record<string, string>;
  multi?: Record<string, string[]>;
  trueFalse?: Record<string, boolean>;
  /** 第二部分 — optional, only when the client went on to set targets. */
  goals?: CoverGoalAnswers;
};

const DISC_ORDER: DiscLetter[] = ['D', 'I', 'S', 'C'];
const ALL_PICK_QUESTIONS: PickQuestion[] = [
  ...PERSONALITY_QUESTIONS,
  ...LIFE_QUESTIONS,
  ...DECISION_QUESTIONS,
  ...SERVICE_QUESTIONS
];

const pickedOption = (answers: CoverQuizAnswers, id: string): QuizOption | undefined => {
  const key = answers.picks?.[id];
  const question = ALL_PICK_QUESTIONS.find(q => q.id === id);
  return key && question ? optionsFor(question, answers.persona).find(o => o.key === key) : undefined;
};

const multiOptions = (answers: CoverQuizAnswers, question: MultiQuestion): QuizOption[] => {
  const keys = answers.multi?.[question.id] || [];
  const options = optionsFor(question, answers.persona);
  return keys.map(k => options.find(o => o.key === k)).filter((o): o is QuizOption => Boolean(o) && o!.key !== 'none');
};

/**
 * The DISC letter. A5 answers it outright (2 points); A1, A2, C2, E3 and S4
 * lean it (1 point per letter). A tie goes to A5 —
 * the travel question is the least guessable of the set.
 */
export const deriveCoverDisc = (answers: CoverQuizAnswers): DiscLetter | null => {
  const score: Record<DiscLetter, number> = { D: 0, I: 0, S: 0, C: 0 };
  let any = false;
  ALL_PICK_QUESTIONS.forEach(q => {
    const option = pickedOption(answers, q.id);
    if (!option) return;
    if (option.disc) {
      score[option.disc] += 2;
      any = true;
    }
    (option.leans || []).forEach(letter => {
      score[letter] += 1;
      any = true;
    });
  });
  if (!any) return null;
  const best = Math.max(...DISC_ORDER.map(l => score[l]));
  const leaders = DISC_ORDER.filter(l => score[l] === best);
  const a5 = pickedOption(answers, 'A5')?.disc;
  return a5 && leaders.includes(a5) ? a5 : leaders[0];
};

/**
 * Why the quiz predicts an objection. Each key is an answer, localised as
 * `cover-quiz-reason-<key>`; the objection's own name and answer are Smart
 * IDEA's `smart-idea-objection-<id>` copy — one vocabulary, two readers.
 */
export type CoverReasonKey = 'E1-partner' | 'E1-family' | 'E1-parents' | 'E1-research' | 'E3-unaware' | 'E4-claims';

export const coverReasonMessageId = (key: CoverReasonKey) => `cover-quiz-reason-${key}`;

export type CoverQuizSignals = {
  persona: CoverPersona | null;
  role: CoverRole | null;
  disc: DiscLetter | null;
  knowledge: { score: number; total: number; wrong: TrueFalseQuestion[] };
  /** C6, the cover they want to hear about, in the order picked. */
  interests: QuizOption[];
  /** D1 — also opening topics. */
  life: QuizOption[];
  /** Answers a meeting can open on, in the language asked for. */
  topics: string[];
  /**
   * Why this client may push back — one entry per objection, with every answer
   * that points at it. Several answers are one source (the quiz), not several.
   */
  objections: { id: ClientObjectionId; reasons: CoverReasonKey[] }[];
  /** How to behave in the room rather than what to say, in the language asked for. */
  cautions: string[];
  /** 你希望的服務方式, in question order; only what was answered. */
  service: { id: string; short: Txt; option: QuizOption }[];
};

const CAUTIONS = {
  pushy: L(
    '怕被推銷：先了解需要，不要急於介紹產品',
    '怕被推销：先了解需要，不要急于介绍产品',
    "Worried about a sales pitch: understand their needs first, don't rush into products"
  ),
  complex: L(
    '覺得保險複雜：少用術語，多用例子',
    '觉得保险复杂：少用术语，多用例子',
    'Finds insurance complicated: less jargon, more examples'
  ),
  noPush: L(
    '最重視不催促、不推銷：給他時間考慮，不要追問決定',
    '最重视不催促、不推销：给他时间考虑，不要追问决定',
    "Values no rushing and no hard sell most: give them time, don't press for a decision"
  )
};

const TOPIC_PREFIX = {
  dependants: L('🤝 最依靠他：', '🤝 最依靠他：', '🤝 Relies on them most: '),
  tenYears: L('十年後想：', '十年后想：', 'In ten years: '),
  windfall: L('收到 10 萬元會', '收到 10 万元会', 'With $100,000 would: ')
};

/** `lang` words the adviser-facing lines (`topics`, `cautions`); the rest is data. */
export const coverQuizSignals = (answers: CoverQuizAnswers, lang: QuizLang): CoverQuizSignals => {
  const picks = answers.picks || {};
  const tf = answers.trueFalse || {};

  const answered = TRUE_FALSE_QUESTIONS.filter(q => typeof tf[q.id] === 'boolean');
  const wrong = answered.filter(q => tf[q.id] !== q.answer);

  const objections: CoverQuizSignals['objections'] = [];
  const push = (id: ClientObjectionId, reason: CoverReasonKey) => {
    const existing = objections.find(o => o.id === id);
    if (existing) existing.reasons.push(reason);
    else objections.push({ id, reasons: [reason] });
  };
  if (picks.E1 === 'partner') push('spouse', 'E1-partner');
  if (picks.E1 === 'family') push('spouse', 'E1-family');
  if (picks.E1 === 'parents') push('spouse', 'E1-parents');
  // Researching online means comparing, so the adviser should arrive with the
  // comparison ready (smart-idea-objection-compare).
  if (picks.E1 === 'research') push('compare', 'E1-research');
  if (picks.E3 === 'unaware') push('price', 'E3-unaware');
  if (picks.E4 === 'claims') push('distrust', 'E4-claims');

  const cautions: string[] = [];
  if (picks.E4 === 'pushy') cautions.push(CAUTIONS.pushy[lang]);
  if (picks.E4 === 'complex') cautions.push(CAUTIONS.complex[lang]);
  if (picks.S5 === 'noPush') cautions.push(CAUTIONS.noPush[lang]);

  const service = SERVICE_QUESTIONS.map(q => ({
    id: q.id,
    short: q.short,
    option: pickedOption(answers, q.id)
  })).filter((s): s is CoverQuizSignals['service'][number] => Boolean(s.option));

  const life = multiOptions(answers, FAMILY_QUESTIONS[0]);
  const topics: string[] = [];
  life.forEach(o => topics.push(`${o.icon} ${o.label[lang]}`));
  const dependants = multiOptions(answers, FAMILY_QUESTIONS[1]);
  if (dependants.length) {
    topics.push(
      `${TOPIC_PREFIX.dependants[lang]}${listJoin(
        dependants.map(o => o.label[lang]),
        lang
      )}`
    );
  }
  const goal = pickedOption(answers, 'C3');
  if (goal) topics.push(`${goal.icon} ${TOPIC_PREFIX.tenYears[lang]}${goal.label[lang]}`);
  const money = pickedOption(answers, 'C2');
  if (money) topics.push(`${money.icon} ${TOPIC_PREFIX.windfall[lang]}${money.label[lang].toLowerCase()}`);

  return {
    persona: answers.persona || null,
    role: answers.role || null,
    disc: deriveCoverDisc(answers),
    knowledge: { score: answered.length - wrong.length, total: TRUE_FALSE_QUESTIONS.length, wrong },
    interests: multiOptions(answers, INTEREST_QUESTION),
    life,
    topics,
    objections,
    cautions,
    service
  };
};

/** The badge on the result card for the 真假挑戰 score. */
export const knowledgeBadge = (score: number, total: number) =>
  score >= total
    ? { emoji: '🏆', name: L('常識達人', '常识达人', 'Money-smart pro') }
    : score > 0
    ? { emoji: '🧭', name: L('常識識途者', '常识识途者', 'Money-smart explorer') }
    : { emoji: '🌱', name: L('常識新手', '常识新手', 'Money-smart beginner') };
